import { useEffect, useRef, useState } from 'react';
import { uploadFoto, ladeFotoUrl } from '../services/mlService';

/**
 * ⚠️ Schaden erfassen — bewusst unauffällig: nur im Bearbeiten-Formular, standardmäßig zugeklappt.
 * Beschädigt markieren, Notiz, bis zu 5 Fotos. Hilft bei Reklamationen beim Paketdienst.
 * Die Fotos liegen wie das Erfassungsfoto beim Erkennungsdienst und werden mit dem Paket gelöscht.
 *
 * value: { beschaedigt, notiz, fotos: [bildId], am } | null — onChange(neu) bei jeder Änderung.
 * onHochgeladen(bildId): meldet neu hochgeladene Fotos (der Aufrufer räumt sie weg, wenn nicht gespeichert wird).
 */
const MAX_FOTOS = 5;

function Vorschau({ id, onWeg, onZeigen, t }) {
  const [url, setUrl] = useState('');
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let lebt = true;
    ladeFotoUrl(id, 'thumb').then((u) => { if (lebt) setUrl(u); }).catch(() => { if (lebt) setFehler(true); });
    return () => { lebt = false; };
  }, [id]);
  return (
    <div style={{ position: 'relative', width: 72, height: 72 }}>
      <button type="button" data-testid="schaden-foto" onClick={() => url && onZeigen(id)} aria-label={t('damage.photo')}
        style={{ width: 72, height: 72, padding: 0, border: '1px solid #ccc', borderRadius: 8, overflow: 'hidden', background: '#eee', cursor: 'pointer' }}>
        {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <span style={{ fontSize: 12 }}>{fehler ? '⚠️' : '⏳'}</span>}
      </button>
      <button type="button" data-testid="schaden-foto-weg" onClick={onWeg} aria-label={t('damage.removePhoto')}
        style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', border: 'none',
          background: '#e5484d', color: '#fff', fontSize: 13, lineHeight: '22px', padding: 0, cursor: 'pointer' }}>×</button>
    </div>
  );
}

export default function SchadenErfassen({ value, onChange, onHochgeladen, t, colors }) {
  const dark = colors?.darkMode;
  const s = value || { beschaedigt: false, notiz: '', fotos: [] };
  const vorhanden = !!(value && (value.beschaedigt || value.notiz || (value.fotos || []).length));
  const [offen, setOffen] = useState(vorhanden);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [gross, setGross] = useState(null); // Bild-URL in voller Größe
  const input = useRef(null);
  const farbe = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff' };

  const setze = (patch) => onChange({ ...s, fotos: s.fotos || [], ...patch, am: new Date().toISOString() });

  const dazu = async (e) => {
    const datei = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!datei) return;
    setFehler(''); setLaedt(true);
    try {
      const id = await uploadFoto(datei);
      if (onHochgeladen) onHochgeladen(id);
      setze({ fotos: [...(s.fotos || []), id].slice(0, MAX_FOTOS) });
    } catch {
      setFehler(t('damage.uploadFailed'));
    }
    setLaedt(false);
  };
  const zeigen = async (id) => { try { setGross(await ladeFotoUrl(id, 'full')); } catch { /* */ } };

  return (
    <div data-testid="schaden" style={{ marginTop: 14 }}>
      <button type="button" data-testid="schaden-toggle" aria-expanded={offen} onClick={() => setOffen(!offen)}
        style={{ background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          color: vorhanden ? '#d9822b' : farbe.sub }}>
        {offen ? '▾' : '▸'} ⚠️ {vorhanden ? t('damage.documented', { n: (s.fotos || []).length }) : t('damage.title')}
      </button>
      {offen && (
        <div style={{ padding: '8px 10px', marginTop: 4, borderRadius: 10, border: `1px solid ${farbe.rand}`, color: farbe.text }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" data-testid="schaden-beschaedigt" checked={!!s.beschaedigt}
              onChange={(e) => setze({ beschaedigt: e.target.checked })} style={{ width: 18, height: 18 }} />
            {t('damage.damaged')}
          </label>
          <textarea data-testid="schaden-notiz" value={s.notiz || ''} maxLength={500} rows={2}
            onChange={(e) => setze({ notiz: e.target.value })} placeholder={t('damage.notePlaceholder')}
            style={{ width: '100%', marginTop: 8, padding: 8, borderRadius: 8, border: `1px solid ${farbe.rand}`, fontSize: 14,
              background: farbe.feld, color: farbe.text, boxSizing: 'border-box', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
            {(s.fotos || []).map((id) => (
              <Vorschau key={id} id={id} t={t} onZeigen={zeigen}
                onWeg={() => setze({ fotos: (s.fotos || []).filter((x) => x !== id) })} />
            ))}
            {(s.fotos || []).length < MAX_FOTOS && (
              <button type="button" data-testid="schaden-foto-dazu" disabled={laedt} onClick={() => input.current && input.current.click()}
                style={{ width: 72, height: 72, borderRadius: 8, border: `1px dashed ${farbe.rand}`, background: 'transparent',
                  color: farbe.sub, fontSize: 12, cursor: 'pointer', lineHeight: 1.3, whiteSpace: 'pre-line' }}>
                {laedt ? '⏳' : `📷\n${t('damage.addPhoto')}`}
              </button>
            )}
            <input ref={input} data-testid="schaden-foto-input" type="file" accept="image/*" capture="environment" onChange={dazu} style={{ display: 'none' }} />
          </div>
          {fehler && <div data-testid="schaden-fehler" style={{ color: '#e5484d', fontSize: 13, marginTop: 6 }}>⚠️ {fehler}</div>}
          <div style={{ fontSize: 12, color: farbe.sub, marginTop: 8, lineHeight: 1.45 }}>{t('damage.hint')}</div>
        </div>
      )}
      {gross && (
        <div onClick={() => setGross(null)} data-testid="schaden-gross"
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <img src={gross} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
